/**
 * RoadmapHistoryPage — /career-transition/roadmap-history
 *
 * Lists all roadmaps the user has ever generated, newest first. Each row
 * links to /career-transition/roadmap/:id. Useful for Pro+ / All Access
 * users who regenerate to track how their plan evolves over time.
 */

import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import HistoryIcon from "@mui/icons-material/History";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { getRoadmapHistory, RoadmapHistoryItem } from "../api/client";
import { ROADMAP_ARCHETYPES } from "../data/careerTransition";
import { L } from "../utils/localized";

// coral via theme.palette.primary — per-product accent deferred (spec D2)

// AUDIENCE_LABELS is a factory so its display strings can be translated —
// call getAudienceLabels(t) inside the component, which has a translation hook.
function getAudienceLabels(t: TFunction): Record<string, string> {
  return {
    swe_to_ai: t("RoadmapHistoryPage.audienceSweToAi"),
    pm_to_ai_pm: t("RoadmapHistoryPage.audiencePmToAiPm"),
    da_to_ai: t("RoadmapHistoryPage.audienceDaToAi"),
  };
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function RoadmapHistoryPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation("pages-account");
  const AUDIENCE_LABELS = getAudienceLabels(t);
  const ARCHETYPE_LOOKUP = Object.fromEntries(
    L(ROADMAP_ARCHETYPES, i18n.language).map((a) => [a.key, a]),
  );
  const { data, isLoading, error } = useQuery<RoadmapHistoryItem[]>({
    queryKey: ["roadmap-history"],
    queryFn: getRoadmapHistory,
    retry: false,
  });

  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100vh", pb: 10 }}>
      {/* Hero */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
          borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
          py: { xs: 4, md: 5 },
        }}
      >
        <Container maxWidth="lg">
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/career-transition")}
            sx={{ color: theme.palette.primary.main, mb: 2 }}
          >
            {t("RoadmapHistoryPage.backToCareerTransitionButton")}
          </Button>
          <Stack direction="row" spacing={1.5} alignItems="center" mb={1}>
            <HistoryIcon sx={{ color: theme.palette.primary.main }} />
            <Typography
              variant="caption"
              fontWeight={700}
              sx={{ color: theme.palette.primary.main, letterSpacing: "0.08em", textTransform: "uppercase" }}
            >
              {t("RoadmapHistoryPage.eyebrow")}
            </Typography>
          </Stack>
          <Typography variant="h3" fontWeight={500} sx={{ letterSpacing: "-0.02em" }}>
            {t("RoadmapHistoryPage.heading")}
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400, mt: 1, maxWidth: 720 }}>
            {t("RoadmapHistoryPage.subheading")}
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ py: { xs: 4, md: 5 } }}>
        {isLoading && (
          <Stack alignItems="center" py={6}>
            <CircularProgress sx={{ color: theme.palette.primary.main }} />
          </Stack>
        )}

        {error && !isLoading && (
          <Alert severity="error">
            {t("RoadmapHistoryPage.loadErrorMessage")}
          </Alert>
        )}

        {!isLoading && !error && data && data.length === 0 && (
          <Paper sx={{ p: 4, textAlign: "center", border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}` }}>
            <Typography variant="h6" fontWeight={500} mb={1}>
              {t("RoadmapHistoryPage.noRoadmapsHeading")}
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              {t("RoadmapHistoryPage.noRoadmapsBody")}
            </Typography>
            <Button
              variant="contained"
              startIcon={<AutoAwesomeIcon />}
              onClick={() => navigate("/career-transition/assessment")}
              sx={{ bgcolor: theme.palette.primary.main, "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.85) } }}
            >
              {t("RoadmapHistoryPage.startAssessmentButton")}
            </Button>
          </Paper>
        )}

        {!isLoading && data && data.length > 0 && (
          <Stack spacing={2}>
            {data.map((r, i) => {
              const archetype = ARCHETYPE_LOOKUP[r.archetype_key];
              const audienceLabel = AUDIENCE_LABELS[r.target_audience] ?? r.target_audience;
              const scoreColor =
                r.readiness_score >= 71
                  ? theme.palette.success.main
                  : r.readiness_score >= 51
                  ? theme.palette.warning.main
                  : r.readiness_score >= 31
                  ? theme.palette.warning.dark   // orange — AMBIGUOUS: warning.dark
                  : theme.palette.error.main;
              return (
                <Paper
                  key={r.id}
                  onClick={() => navigate(`/career-transition/roadmap/${r.id}`)}
                  sx={{
                    p: 2.5,
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                    borderLeft: `4px solid ${scoreColor}`,
                    cursor: "pointer",
                    transition: "all 0.15s",
                    "&:hover": {
                      borderColor: theme.palette.primary.main,
                      boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.12)}`,
                    },
                  }}
                >
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" spacing={1} flexWrap="wrap" gap={0.5} mb={1}>
                        {i === 0 && (
                          <Chip
                            label={t("RoadmapHistoryPage.latestChip")}
                            size="small"
                            sx={{
                              fontWeight: 500,
                              fontSize: "0.6rem",
                              height: 18,
                              bgcolor: theme.palette.primary.main,
                              color: theme.palette.background.paper,
                            }}
                          />
                        )}
                        <Chip
                          label={audienceLabel}
                          size="small"
                          sx={{ fontWeight: 500, fontSize: "0.65rem", height: 18 }}
                        />
                        {archetype && (
                          <Chip
                            label={archetype.name.toUpperCase()}
                            size="small"
                            sx={{
                              fontWeight: 500,
                              fontSize: "0.6rem",
                              height: 18,
                              bgcolor: alpha(theme.palette.primary.main, 0.1),
                              color: theme.palette.primary.main,
                            }}
                          />
                        )}
                      </Stack>
                      <Typography variant="body1" fontWeight={500}>
                        {archetype?.description ?? t("RoadmapHistoryPage.fallbackRoadmapName", { name: archetype?.name ?? r.archetype_key })}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.3, display: "block" }}>
                        {t("RoadmapHistoryPage.generatedOn", { date: formatDate(r.created_at) })}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                      }}
                    >
                      <Box sx={{ textAlign: "right" }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                          {t("RoadmapHistoryPage.readinessLabel")}
                        </Typography>
                        <Typography
                          variant="h5"
                          fontWeight={500}
                          sx={{ color: scoreColor, lineHeight: 1 }}
                        >
                          {r.readiness_score}
                          <Typography
                            component="span"
                            variant="caption"
                            sx={{ color: "text.secondary", fontWeight: 400 }}
                          >
                            {t("RoadmapHistoryPage.scoreSuffix")}
                          </Typography>
                        </Typography>
                      </Box>
                      <ArrowForwardIcon sx={{ color: theme.palette.primary.main }} />
                    </Box>
                  </Stack>
                </Paper>
              );
            })}
            {/* Regenerate CTA at bottom */}
            <Paper sx={{ p: 2.5, bgcolor: alpha(theme.palette.primary.main, 0.04), textAlign: "center", mt: 2 }}>
              <Button
                variant="contained"
                startIcon={<AutoAwesomeIcon />}
                onClick={() => navigate("/career-transition/assessment")}
                sx={{ bgcolor: theme.palette.primary.main, "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.85) } }}
              >
                {t("RoadmapHistoryPage.generateNewRoadmapButton")}
              </Button>
            </Paper>
          </Stack>
        )}
      </Container>
    </Box>
  );
}
